import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useMemo, useState } from "react";
import Http from "../../../Common/Http";

/**
 * Day View — the REAL history of one day, per developer.
 *
 * Reads GET /tasks/day-timeline (server-computed from attendance + task status
 * history — the same engine as every other report), and draws, per developer:
 *
 *   - the office band (real check-in → check-out, not an assumed 9:30–19:30),
 *   - a colored block for every stretch a task was truly In Progress
 *     (start ▶ / resume ↻; ended by pause, switch, done, review, or running),
 *   - red striped blocks while a task sat paused, gray for idle office time,
 *   - check-in / check-out / "now" markers,
 *   - an expandable chronological day log (arrived, started, switched, paused,
 *     resumed, completed, left).
 *
 * The hour axis starts at the earliest real check-in (not midnight), very short
 * stretches get a minimum visual width, and bursts of near-instant status
 * changes (bulk updates) are folded into one "quick updates" block.
 */

// ---------- time helpers (all server times are "YYYY-MM-DD HH:mm:ss") ----------

const parseDT = (s) => {
  if (!s) return null;
  const [d, t] = s.split(" ");
  const [y, m, dd] = d.split("-").map(Number);
  const [h, mi, se] = (t || "00:00:00").split(":").map(Number);
  return new Date(y, m - 1, dd, h, mi, se || 0);
};

const minutesOfDay = (dt) => dt.getHours() * 60 + dt.getMinutes() + dt.getSeconds() / 60;

const fmtClock = (dtOrStr) => {
  const dt = typeof dtOrStr === "string" ? parseDT(dtOrStr) : dtOrStr;
  if (!dt) return "—";
  let h = dt.getHours();
  const m = dt.getMinutes();
  const p = h < 12 ? "am" : "pm";
  h = h % 12 || 12;
  return m === 0 ? `${h}${p}` : `${h}:${String(m).padStart(2, "0")}${p}`;
};

const fmtHourTick = (min) => {
  let h = Math.floor(min / 60);
  const p = h < 12 ? "am" : "pm";
  const hh = h % 12 || 12;
  return min % 60 === 0 ? `${hh}${p}` : `${hh}:${String(min % 60).padStart(2, "0")}${p}`;
};

const fmtDur = (seconds) => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2, "0")}m`;
};

const fmtHours = (seconds) => (seconds > 0 ? `${(seconds / 3600).toFixed(1)}h` : "0h");

const dateToYMD = (dateObj) => {
  const d = new Date(dateObj);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Blocks are colored by the task's CURRENT status, with the exact same colors
// as the Backlog page — so "green means Done" holds everywhere in the app.
const STATUS_COLORS = {
  "To Do": "#6c757d", // gray
  "In Progress": "#0d6efd", // blue
  Pause: "#dc3545", // red
  Review: "#fd7e14", // orange
  Done: "#198754", // green
};

// Bursts of near-instant status flips (bulk updates) fold into one block.
const MICRO_SECONDS = 180; // a "micro" stretch is shorter than this
const MICRO_GAP_MIN = 2; // and joins a burst when within this many minutes
const CLUSTER_MIN_COUNT = 3;

const END_REASON_LABEL = {
  paused: "paused",
  switched: "switched to another task (auto-paused)",
  done: "completed",
  review: "sent to review",
  running: "still running now",
  continues: "continues past this day",
  unknown: "no closing record",
};

const EVENT_META = {
  check_in: { icon: "pi-sign-in", color: "#059669", text: () => "Arrived — checked in" },
  check_out: { icon: "pi-sign-out", color: "#475569", text: () => "Left — checked out" },
  start: { icon: "pi-play", color: "#2563eb", text: (e) => `Started “${e.task_title}”` },
  resume: { icon: "pi-replay", color: "#0d9488", text: (e) => `Resumed “${e.task_title}”` },
  pause: { icon: "pi-pause", color: "#dc2626", text: (e) => `Paused “${e.task_title}”` },
  switch: { icon: "pi-arrow-right-arrow-left", color: "#dc2626", text: (e) => `“${e.task_title}” auto-paused (switched task)` },
  done: { icon: "pi-check-circle", color: "#16a34a", text: (e) => `Completed “${e.task_title}”` },
  review: { icon: "pi-eye", color: "#ca8a04", text: (e) => `Sent “${e.task_title}” to review` },
};

const DayView = ({ date, tasks, onTaskClick, developer, searchTerm }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openLogs, setOpenLogs] = useState({});

  const dateStr = dateToYMD(date);
  const isToday = dateStr === dateToYMD(new Date());

  useEffect(() => {
    let cancelled = false;
    const load = async (silent) => {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const res = await Http.get(`/tasks/day-timeline?date=${dateStr}`);
        if (!cancelled) setData(res.data && res.data.data ? res.data.data : res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || err.message || "Failed to load day timeline");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load(false);
    // Live day: refresh quietly every minute so "now" stays honest.
    const timer = isToday ? setInterval(() => load(true), 60000) : null;
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [dateStr]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- derive everything drawable from the payload ----------

  const model = useMemo(() => {
    if (!data || !data.developers) return null;

    let devs = data.developers;
    if (developer) devs = devs.filter((d) => d.developer_id === developer);

    // The visible day starts when people actually arrived: bounds come from
    // real attendance. Task time outside them (overnight carry-over, forgot
    // check-out tails) is clipped — it is faded off-attendance time anyway.
    // Only when nobody has attendance do the segments define the bounds.
    let minM = Infinity;
    let maxM = -Infinity;
    devs.forEach((d) => {
      d.attendance.forEach((a) => {
        minM = Math.min(minM, minutesOfDay(parseDT(a.check_in)));
        if (a.effective_check_out) maxM = Math.max(maxM, minutesOfDay(parseDT(a.effective_check_out)));
      });
    });
    if (!isFinite(minM)) {
      devs.forEach((d) => {
        d.segments.forEach((s) => {
          minM = Math.min(minM, minutesOfDay(parseDT(s.start)));
          maxM = Math.max(maxM, minutesOfDay(parseDT(s.end)));
        });
      });
    }
    if (!isFinite(minM)) {
      minM = 9 * 60;
      maxM = 19 * 60;
    }
    const viewStart = Math.max(0, Math.floor(minM / 60) * 60 - 30);
    let viewEnd = Math.min(24 * 60, Math.ceil(maxM / 60) * 60 + 30);
    if (viewEnd - viewStart < 3 * 60) viewEnd = Math.min(24 * 60, viewStart + 3 * 60);
    const span = viewEnd - viewStart;
    const pct = (min) => ((min - viewStart) / span) * 100;
    const clampM = (m) => Math.max(viewStart, Math.min(viewEnd, m));

    // A block is never drawn narrower than this many minutes, so a 30-second
    // task is still visible and hoverable.
    const minVisMin = Math.max(3, span * 0.008);

    // Hour ticks (thin the labels when the day is long).
    const ticks = [];
    const firstTick = Math.ceil(viewStart / 60) * 60;
    const labelEvery = span > 12.5 * 60 ? 120 : 60;
    for (let m = firstTick; m <= viewEnd; m += 60) {
      ticks.push({ min: m, left: pct(m), label: m % labelEvery === 0 ? fmtHourTick(m) : null });
    }

    const rows = devs.map((d) => {
      const segs = d.segments.map((s) => ({
        ...s,
        sM: minutesOfDay(parseDT(s.start)),
        eM: Math.max(minutesOfDay(parseDT(s.end)), minutesOfDay(parseDT(s.start)) + 0.001),
      }));

      // Fold bursts of near-instant changes (bulk updates) into one block.
      const items = [];
      let run = [];
      const flushRun = () => {
        if (run.length >= CLUSTER_MIN_COUNT) {
          items.push({
            type: "cluster",
            sM: run[0].sM,
            eM: run[run.length - 1].eM,
            start: run[0].start,
            end: run[run.length - 1].end,
            children: run,
          });
        } else {
          run.forEach((s) => items.push({ type: "seg", ...s }));
        }
        run = [];
      };
      segs.forEach((s) => {
        const isMicro = s.seconds < MICRO_SECONDS;
        if (isMicro && (run.length === 0 || s.sM - run[run.length - 1].eM <= MICRO_GAP_MIN)) {
          run.push(s);
        } else {
          flushRun();
          if (isMicro) run.push(s);
          else items.push({ type: "seg", ...s });
        }
      });
      flushRun();

      // Visual extents, clipped to the axis with the minimum width.
      const prepSeg = (it) => {
        it.vS = clampM(it.sM);
        it.vE = Math.max(clampM(it.eM), Math.min(viewEnd, it.vS + minVisMin));
        it.clippedLeft = it.sM < viewStart - 0.5;
        it.clippedRight = it.eM > viewEnd + 0.5;
        return it;
      };
      items.forEach(prepSeg);

      // ---- one lane per task: the "task bar" ----
      // All stretches of one task share a lane, so the bar reads as the task's
      // status over time: blue while In Progress, striped red while it sat
      // paused, a ✓ / review marker at the moment it closed.
      const attEndM = d.attendance.reduce(
        (mx, a) => (a.effective_check_out ? Math.max(mx, minutesOfDay(parseDT(a.effective_check_out))) : mx),
        -Infinity
      );

      const groups = [];
      items.forEach((it) => {
        if (it.type === "cluster") groups.push({ kind: "cluster", item: it, gS: it.vS, gE: it.vE });
      });

      const byTask = {};
      items.forEach((it) => {
        if (it.type === "seg") (byTask[it.task_id] = byTask[it.task_id] || []).push(it);
      });
      Object.keys(byTask).forEach((tid) => {
        const list = byTask[tid].sort((a, b) => a.vS - b.vS);
        const pieces = [];
        list.forEach((s, i) => {
          pieces.push({ piece: "seg", ...s, isLastOfTask: i === list.length - 1 });
          if (s.end_reason === "paused" || s.end_reason === "switched") {
            // Red until it was resumed — or, if never resumed this day, until
            // the office day ended.
            const until = i + 1 < list.length ? list[i + 1].vS : Math.min(viewEnd, Math.max(attEndM, s.vE));
            if (until - s.vE > 0.5) {
              pieces.push({
                piece: "pause",
                sM: s.vE,
                eM: until,
                task_id: s.task_id,
                title: s.title,
                sinceStr: s.end,
                resumed: i + 1 < list.length,
              });
            }
          }
          if (s.end_reason === "done" || s.end_reason === "review") {
            pieces.push({ piece: "mark", kind: s.end_reason, at: s.vE, task_id: s.task_id, title: s.title, atStr: s.end });
          }
        });
        const extent = (p) => (p.piece === "pause" ? p.eM : p.piece === "mark" ? p.at + 0.1 : p.vE);
        groups.push({ kind: "task", pieces, gS: pieces[0].vS, gE: Math.max(...pieces.map(extent)) });
      });

      // Greedy lane assignment over whole groups (task bars stay intact).
      groups.sort((a, b) => a.gS - b.gS);
      const lanes = [];
      groups.forEach((g) => {
        let lane = lanes.findIndex((endM) => endM <= g.gS + 0.25);
        if (lane === -1) {
          lane = lanes.length;
          lanes.push(g.gE);
        } else {
          lanes[lane] = g.gE;
        }
        g.lane = lane;
        if (g.kind === "cluster") g.item.lane = lane;
        else g.pieces.forEach((p) => (p.lane = lane));
      });
      const laneCount = Math.max(1, lanes.length);

      // Free room (minutes) to the right of each block in its lane — used to
      // write the label BESIDE a block too narrow to hold it inside. Pause
      // connectors and markers count as occupants.
      const occStart = (o) => (o.vS !== undefined ? o.vS : o.sM !== undefined ? o.sM : o.at);
      const occEnd = (o) => (o.vE !== undefined ? o.vE : o.eM !== undefined ? o.eM : o.at);
      const byLane = {};
      groups.forEach((g) => {
        const list = g.kind === "cluster" ? [g.item] : g.pieces;
        list.forEach((o) => (byLane[o.lane] = byLane[o.lane] || []).push(o));
      });
      Object.keys(byLane).forEach((k) => {
        const arr = byLane[k].sort((a, b) => occStart(a) - occStart(b));
        arr.forEach((o, i) => {
          o.extSpace = (i + 1 < arr.length ? occStart(arr[i + 1]) : viewEnd) - occEnd(o);
        });
      });

      // Gaps = office time not covered by any real segment → paused or idle.
      const officeIvs = d.attendance
        .filter((a) => a.effective_check_out)
        .map((a) => [minutesOfDay(parseDT(a.check_in)), minutesOfDay(parseDT(a.effective_check_out))]);
      const union = [];
      [...segs]
        .sort((a, b) => a.sM - b.sM)
        .forEach(({ sM, eM }) => {
          if (union.length && sM <= union[union.length - 1][1]) {
            union[union.length - 1][1] = Math.max(union[union.length - 1][1], eM);
          } else {
            union.push([sM, eM]);
          }
        });
      const gaps = [];
      officeIvs.forEach(([oS, oE]) => {
        let cursor = oS;
        union.forEach(([uS, uE]) => {
          if (uS > cursor && Math.min(uS, oE) > cursor) gaps.push([cursor, Math.min(uS, oE)]);
          cursor = Math.max(cursor, uE);
        });
        if (cursor < oE) gaps.push([cursor, oE]);
      });
      // Row-level gaps are plain idle now — the paused stretches live inside
      // each task's own bar as red connectors.
      const gapBlocks = gaps
        .filter(([gS, gE]) => gE - gS >= 1) // ignore sub-minute slivers
        .map(([gS, gE]) => ({ sM: gS, eM: gE, seconds: (gE - gS) * 60 }));

      const taskCount = new Set(segs.map((s) => s.task_id)).size;
      const pauseCount = d.events.filter((e) => e.type === "pause" || e.type === "switch").length;

      return { ...d, groups, laneCount, gapBlocks, taskCount, pauseCount };
    });

    // Active people first, then by name.
    rows.sort((a, b) => {
      const score = (r) => (r.groups.length ? 0 : r.attendance.length ? 1 : 2);
      return score(a) - score(b) || a.name.localeCompare(b.name);
    });

    const nowM = minutesOfDay(new Date());
    const showNow = isToday && nowM >= viewStart && nowM <= viewEnd;

    return { rows, ticks, pct, clampM, viewStart, viewEnd, minVisMin, showNow, nowLeft: pct(nowM) };
  }, [data, developer, isToday]);

  // ---------- rendering ----------

  const formattedDate = new Date(date).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  if (loading) {
    return (
      <div className="text-center p-5">
        <ProgressSpinner />
        <p className="mt-3">Loading the real day history…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center" style={{ color: "#b91c1c" }}>
        <i className="pi pi-exclamation-triangle mr-2" />
        {error}
      </div>
    );
  }

  if (!model) return null;

  const term = (searchTerm || "").trim().toLowerCase();
  const matches = (title) => !term || (title || "").toLowerCase().includes(term);

  const LANE_H = 58; // tall lanes: title + time/duration fit comfortably inside
  const PAD_TOP = 24; // room for check-in/out marker labels

  const segTooltip = (s) => {
    const lines = [
      `${s.title}${s.project ? ` — ${s.project}` : ""}`,
      `Status now: ${s.task_status || "unknown"}`,
      `${fmtClock(s.start)} → ${fmtClock(s.end)} (${fmtDur(s.seconds)})`,
      `${s.kind === "resume" ? "Resumed" : "Started"}${s.started_earlier ? " (carried over from a previous day)" : ""}`,
      `Ended: ${END_REASON_LABEL[s.end_reason] || s.end_reason}${
        s.end_reason === "running" || s.end_reason === "continues" ? "" : ` at ${fmtClock(s.end)}`
      }`,
    ];
    if (s.parts.some((p) => !p.in_office)) lines.push("Faded part = outside attendance (not counted as worked)");
    return lines.join("\n");
  };

  const clusterTooltip = (c) => {
    const lines = [
      `${c.children.length} quick task updates, ${fmtClock(c.start)} → ${fmtClock(c.end)}`,
      "(bulk status changes — each lasted seconds)",
      "",
    ];
    c.children.slice(0, 14).forEach((s) => {
      lines.push(`${fmtClock(s.start)}  ${s.title} (${fmtDur(s.seconds)}, ${s.end_reason})`);
    });
    if (c.children.length > 14) lines.push(`… and ${c.children.length - 14} more`);
    return lines.join("\n");
  };

  // Open the details modal with the FULL task fetched fresh — /task/{id}
  // includes the status history (every start/pause/resume with time and
  // comment), which the /tasks/calendar list does not carry. Fall back to the
  // cached calendar task if the fetch fails.
  const handleSegClick = async (s) => {
    if (!onTaskClick) return;
    try {
      const res = await Http.get(`/task/${s.task_id}`);
      const full = res.data && res.data.data ? res.data.data : null;
      if (full) {
        onTaskClick(full);
        return;
      }
    } catch (err) {
      // ignore and fall back
    }
    const cached = (tasks || []).find((t) => t.id === s.task_id);
    if (cached) onTaskClick(cached);
  };

  return (
    <div className="dv2">
      <h2 className="dv2-header">{formattedDate}</h2>

      <div className="dv2-grid">
        {/* Hour scale */}
        <div className="dv2-scale-row">
          <div className="dv2-left" />
          <div className="dv2-track dv2-scale">
            {model.ticks.map((t, i) => (
              <div key={i} className="dv2-tick" style={{ left: `${t.left}%` }}>
                {t.label && <span className="dv2-tick-label">{t.label}</span>}
              </div>
            ))}
          </div>
        </div>

        {model.rows.map((row) => {
          const rowH = PAD_TOP + row.laneCount * LANE_H + 10;
          const absent = !row.attendance.length && !row.groups.length;
          const logOpen = !!openLogs[row.developer_id];
          const logEvents = row.events.filter((e) => EVENT_META[e.type]);

          return (
            <div key={row.developer_id} className="dv2-row">
              {/* Developer info */}
              <div className="dv2-left">
                <div className="dv2-name">{row.name}</div>
                {row.attendance.length > 0 ? (
                  <div className="dv2-att">
                    <i className="pi pi-sign-in" style={{ color: "#059669", fontSize: 11 }} /> {fmtClock(row.attendance[0].check_in)}
                    {"  "}
                    <i className="pi pi-sign-out" style={{ color: "#475569", fontSize: 11, marginLeft: 6 }} />{" "}
                    {row.attendance[row.attendance.length - 1].check_out
                      ? fmtClock(row.attendance[row.attendance.length - 1].check_out)
                      : row.attendance[row.attendance.length - 1].still_in
                      ? "still in"
                      : "—"}
                  </div>
                ) : (
                  <div className="dv2-att dv2-absent">No attendance</div>
                )}
                {row.attendance.some((a) => a.forgot_checkout) && (
                  <div className="dv2-flag">
                    <i className="pi pi-flag-fill" style={{ fontSize: 10, marginRight: 4 }} />
                    forgot checkout
                  </div>
                )}
                {row.attendance.some((a) => a.still_in && a.capped) && (
                  <div className="dv2-flag dv2-flag-soft">
                    <i className="pi pi-flag" style={{ fontSize: 10, marginRight: 4 }} />
                    no checkout yet — counting stopped at 7pm
                  </div>
                )}
                <div className="dv2-hours">
                  <span className="dv2-worked">{fmtHours(row.worked_seconds)} worked</span>
                  {" · "}
                  <span>{fmtHours(row.in_office_seconds)} in office</span>
                </div>
                {(row.taskCount > 0 || row.pauseCount > 0) && (
                  <div className="dv2-hours">
                    {row.taskCount} task{row.taskCount !== 1 ? "s" : ""} · {row.pauseCount} pause{row.pauseCount !== 1 ? "s" : ""}
                  </div>
                )}
                {logEvents.length > 0 && (
                  <button
                    className="dv2-logbtn"
                    onClick={() => setOpenLogs((o) => ({ ...o, [row.developer_id]: !o[row.developer_id] }))}
                  >
                    <i className={`pi ${logOpen ? "pi-chevron-down" : "pi-chevron-right"}`} style={{ fontSize: 10 }} /> Day log (
                    {logEvents.length})
                  </button>
                )}
              </div>

              {/* Timeline track */}
              <div className="dv2-track">
                <div className="dv2-canvas" style={{ height: rowH }}>
                  {/* office band(s) */}
                  {row.attendance
                    .filter((a) => a.effective_check_out)
                    .map((a, i) => {
                      const l = model.pct(model.clampM(minutesOfDay(parseDT(a.check_in))));
                      const r = model.pct(model.clampM(minutesOfDay(parseDT(a.effective_check_out))));
                      return (
                        <React.Fragment key={i}>
                          <div
                            className={`dv2-office ${a.forgot_checkout ? "dv2-office-forgot" : ""}`}
                            style={{ left: `${l}%`, width: `${Math.max(r - l, 0.2)}%` }}
                            title={`In office ${fmtClock(a.check_in)} → ${
                              a.check_out
                                ? fmtClock(a.check_out)
                                : a.still_in
                                ? a.capped
                                  ? "7pm (no checkout yet — counting stopped at 7pm)"
                                  : "now (still in)"
                                : `${fmtClock(a.effective_check_out)} (forgot to check out — counted till 7pm max)`
                            }`}
                          />
                          <div className="dv2-marker dv2-in" style={{ left: `${l}%` }}>
                            <span className="dv2-marker-label" style={{ color: "#059669" }}>
                              {fmtClock(a.check_in)}
                            </span>
                          </div>
                          <div
                            className={`dv2-marker ${a.forgot_checkout ? "dv2-out-forgot" : a.still_in ? "dv2-out-open" : "dv2-out"}`}
                            style={{ left: `${r}%` }}
                          >
                            <span className="dv2-marker-label" style={{ color: a.forgot_checkout ? "#dc2626" : "#475569" }}>
                              {a.forgot_checkout ? (
                                <>
                                  <i className="pi pi-flag-fill" style={{ fontSize: 9, marginRight: 2 }} />
                                  {fmtClock(a.effective_check_out)}
                                </>
                              ) : a.still_in ? (
                                a.capped ? fmtClock(a.effective_check_out) : "now"
                              ) : (
                                fmtClock(a.check_out)
                              )}
                            </span>
                          </div>
                        </React.Fragment>
                      );
                    })}

                  {/* idle gaps (paused stretches live inside each task bar) */}
                  {row.gapBlocks.map((g, i) => {
                    const l = model.pct(model.clampM(g.sM));
                    const w = Math.max(model.pct(model.clampM(g.eM)) - l, 0.15);
                    return (
                      <div
                        key={`g${i}`}
                        className="dv2-gap-idle"
                        style={{ left: `${l}%`, width: `${w}%`, top: PAD_TOP, height: row.laneCount * LANE_H }}
                        title={`No active task — ${fmtDur(g.seconds)}`}
                      >
                        {w > 4 && <span className="dv2-gap-label">idle</span>}
                      </div>
                    );
                  })}

                  {/* task bars (status over time) + quick-update clusters */}
                  {row.groups.map((grp, gi) => {
                    if (grp.kind === "cluster") {
                      const it = grp.item;
                      const top = PAD_TOP + it.lane * LANE_H + 3;
                      const h = LANE_H - 6;
                      const l = model.pct(it.vS);
                      const w = Math.max(model.pct(it.vE) - l, 0.15);
                      const extW = ((it.extSpace || 0) / (model.viewEnd - model.viewStart)) * 100;
                      const labelInside = w > 3.2;
                      const labelBeside = !labelInside && extW > 2.2;
                      const dim = term && !it.children.some((s) => matches(s.title));
                      return (
                        <React.Fragment key={`c${gi}`}>
                          <div
                            className="dv2-cluster"
                            style={{ left: `${l}%`, width: `${w}%`, top, height: h, opacity: dim ? 0.15 : 1 }}
                            title={clusterTooltip(it)}
                          >
                            {labelInside && (
                              <div className="dv2-seg-line1">
                                <i className="pi pi-bolt" style={{ fontSize: 9, marginRight: 3 }} />
                                {it.children.length} quick updates
                              </div>
                            )}
                            {w > 4 && (
                              <div className="dv2-seg-line2">
                                {fmtClock(it.start)} – {fmtClock(it.end)}
                              </div>
                            )}
                          </div>
                          {labelBeside && (
                            <div
                              className="dv2-ext"
                              style={{ left: `calc(${l + w}% + 3px)`, width: `${Math.min(extW, 30) - 0.4}%`, top, height: h, opacity: dim ? 0.2 : 1 }}
                              title={clusterTooltip(it)}
                            >
                              <span className="dv2-ext-dot" style={{ background: "#475569" }} />
                              <span className="dv2-ext-text">
                                {fmtClock(it.start)} · {it.children.length} quick updates
                              </span>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    }

                    return grp.pieces.map((p, pi) => {
                      const key = `t${gi}p${pi}`;
                      const top = PAD_TOP + p.lane * LANE_H + 3;
                      const h = LANE_H - 6;

                      // Red connector: the task sat paused through this stretch.
                      if (p.piece === "pause") {
                        const l = model.pct(p.sM);
                        const w = Math.max(model.pct(p.eM) - l, 0.15);
                        const dim = !matches(p.title);
                        const secs = (p.eM - p.sM) * 60;
                        return (
                          <div
                            key={key}
                            className="dv2-pausebar"
                            style={{ left: `${l}%`, width: `${w}%`, top, height: h, opacity: dim ? 0.15 : 1 }}
                            title={`“${p.title}” paused since ${fmtClock(p.sinceStr)}${
                              p.resumed ? "" : " — not resumed this day"
                            } · ${fmtDur(secs)}`}
                          >
                            {w > 3.5 ? (
                              <span className="dv2-pausebar-label">paused {fmtDur(secs)}</span>
                            ) : w > 1.2 ? (
                              <span className="dv2-pausebar-label">{fmtDur(secs)}</span>
                            ) : null}
                          </div>
                        );
                      }

                      // ✓ / review marker at the moment the task closed.
                      if (p.piece === "mark") {
                        const l = model.pct(p.at);
                        const done = p.kind === "done";
                        return (
                          <span
                            key={key}
                            className="dv2-mark"
                            style={{
                              left: `calc(${l}% - 9px)`,
                              top: top + h / 2 - 9,
                              background: done ? STATUS_COLORS.Done : STATUS_COLORS.Review,
                              opacity: matches(p.title) ? 1 : 0.15,
                            }}
                            title={`“${p.title}” ${done ? "completed" : "sent to review"} at ${fmtClock(p.atStr)}`}
                          >
                            <i className={`pi ${done ? "pi-check" : "pi-eye"}`} />
                          </span>
                        );
                      }

                      const s = p;
                      const l = model.pct(s.vS);
                      const w = Math.max(model.pct(s.vE) - l, 0.15);
                      const extW = ((s.extSpace || 0) / (model.viewEnd - model.viewStart)) * 100;
                      const labelInside = w > 3.2;
                      const labelBeside = !labelInside && extW > 2.2;
                      const dim = !matches(s.title);
                      // Working time is blue; if this is the stretch where the
                      // task finished (or the task ended the day Done/Review),
                      // it fades into the final status color — so a completed
                      // task is recognizable at a glance, while live work
                      // stays plain blue.
                      const blue = STATUS_COLORS["In Progress"];
                      const doneEnd = s.end_reason === "done" || (s.isLastOfTask && s.task_status === "Done");
                      const reviewEnd = !doneEnd && (s.end_reason === "review" || (s.isLastOfTask && s.task_status === "Review"));
                      const finalColor = doneEnd ? STATUS_COLORS.Done : reviewEnd ? STATUS_COLORS.Review : blue;
                      const bg =
                        doneEnd || reviewEnd ? `linear-gradient(90deg, ${blue} 0%, ${blue} 40%, ${finalColor} 100%)` : blue;
                      const tooSmall = s.eM - s.sM < model.minVisMin; // shorter than the visual minimum → one solid min-width block
                      return (
                        <React.Fragment key={key}>
                        {tooSmall ? (
                          <div
                            className="dv2-seg"
                            style={{ left: `${l}%`, width: `${w}%`, top, height: h, background: bg, opacity: dim ? 0.15 : 1 }}
                            title={segTooltip(s)}
                            onClick={() => handleSegClick(s)}
                          />
                        ) : (
                          s.parts.map((p, j) => {
                            const pS = model.clampM(minutesOfDay(parseDT(p.start)));
                            const pE = model.clampM(minutesOfDay(parseDT(p.end)));
                            if (pE - pS <= 0.01 && !(j === 0 && s.parts.length === 1)) return null;
                            const pl = model.pct(pS);
                            const pw = Math.max(model.pct(pE) - pl, 0.15);
                            return (
                              <div
                                key={j}
                                className={`dv2-seg ${p.in_office ? "" : "dv2-seg-out"}`}
                                style={{
                                  left: `${pl}%`,
                                  width: `${pw}%`,
                                  top,
                                  height: h,
                                  ...(p.in_office ? { background: bg } : { backgroundColor: blue }),
                                  opacity: dim ? 0.15 : undefined,
                                }}
                                title={segTooltip(s)}
                                onClick={() => handleSegClick(s)}
                              />
                            );
                          })
                        )}
                        {/* label inside the block when it fits, else beside it */}
                        {labelInside && (
                          <div
                            className="dv2-seg-label"
                            style={{ left: `${l}%`, width: `${w}%`, top, height: h, opacity: dim ? 0.2 : 1 }}
                            title={segTooltip(s)}
                            onClick={() => handleSegClick(s)}
                          >
                            <div className="dv2-seg-line1">
                              <i className={`pi ${s.kind === "resume" ? "pi-replay" : "pi-play"}`} style={{ fontSize: 9, marginRight: 4 }} />
                              {s.clippedLeft ? "… " : ""}
                              {s.title}
                            </div>
                            <div className="dv2-seg-line2">
                              {fmtClock(s.start)} – {s.end_reason === "running" ? "now" : fmtClock(s.end)} · {fmtDur(s.seconds)}
                              {s.end_reason === "paused" || s.end_reason === "switched" ? " · ⏸" : s.end_reason === "done" ? " · ✓" : ""}
                            </div>
                          </div>
                        )}
                        {labelBeside && (
                          <div
                            className="dv2-ext"
                            style={{ left: `calc(${l + w}% + 3px)`, width: `${Math.min(extW, 30) - 0.4}%`, top, height: h, opacity: dim ? 0.2 : 1 }}
                            title={segTooltip(s)}
                            onClick={() => handleSegClick(s)}
                          >
                            <span className="dv2-ext-dot" style={{ background: finalColor }} />
                            <span className="dv2-ext-text">
                              {fmtClock(s.start)} {s.title} · {fmtDur(s.seconds)}
                              {s.end_reason === "paused" || s.end_reason === "switched"
                                ? " ⏸"
                                : s.end_reason === "done"
                                ? " ✓"
                                : s.end_reason === "review"
                                ? " → review"
                                : ""}
                            </span>
                          </div>
                        )}
                      </React.Fragment>
                      );
                    });
                  })}

                  {/* now line */}
                  {model.showNow && <div className="dv2-now" style={{ left: `${model.nowLeft}%` }} />}

                  {/* grid lines */}
                  {model.ticks.map((t, i) => (
                    <div key={`t${i}`} className="dv2-grid-line" style={{ left: `${t.left}%` }} />
                  ))}

                  {absent && <div className="dv2-absent-note">Absent — no attendance and no task activity</div>}
                </div>

                {/* Day log */}
                {logOpen && (
                  <div className="dv2-log">
                    {logEvents.map((e, i) => {
                      const meta = EVENT_META[e.type];
                      return (
                        <div key={i} className="dv2-log-item">
                          <span className="dv2-log-time">{fmtClock(e.at)}</span>
                          <i className={`pi ${meta.icon}`} style={{ color: meta.color, fontSize: 11, margin: "0 6px" }} />
                          <span>{meta.text(e)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {model.rows.length === 0 && <div className="p-4 text-center" style={{ color: "#64748b" }}>No developers to show for this day.</div>}
      </div>

      {/* Legend */}
      <div className="dv2-legend">
        <span className="dv2-leg" style={{ fontWeight: 600 }}>
          The bar shows the task's status over time:
        </span>
        <span className="dv2-leg">
          <span className="dv2-leg-swatch" style={{ background: STATUS_COLORS["In Progress"] }} /> working — still In Progress
        </span>
        <span className="dv2-leg">
          <span
            className="dv2-leg-swatch"
            style={{ background: `linear-gradient(90deg, ${STATUS_COLORS["In Progress"]}, ${STATUS_COLORS.Done})`, width: 26 }}
          />{" "}
          fades to green — finally done
        </span>
        <span className="dv2-leg">
          <span className="dv2-leg-swatch dv2-leg-paused" /> paused
        </span>
        <span className="dv2-leg">
          <span className="dv2-leg-mark" style={{ background: STATUS_COLORS.Done }}>
            <i className="pi pi-check" />
          </span>{" "}
          completed
        </span>
        <span className="dv2-leg">
          <span className="dv2-leg-mark" style={{ background: STATUS_COLORS.Review }}>
            <i className="pi pi-eye" />
          </span>{" "}
          sent to review
        </span>
        <span className="dv2-leg">
          <span className="dv2-leg-swatch" style={{ background: "#e2e8f0" }} /> idle (in office, no active task)
        </span>
        <span className="dv2-leg">
          <span className="dv2-leg-swatch" style={{ background: "#ecfdf5", border: "1px solid #a7f3d0" }} /> in office
        </span>
        <span className="dv2-leg">
          <span className="dv2-leg-swatch" style={{ background: "#475569" }} /> <i className="pi pi-bolt" style={{ fontSize: 10 }} /> quick
          bulk updates
        </span>
        <span className="dv2-leg">
          <i className="pi pi-play" style={{ fontSize: 10 }} /> started &nbsp;
          <i className="pi pi-replay" style={{ fontSize: 10 }} /> resumed
        </span>
        <span className="dv2-leg" style={{ color: "#dc2626" }}>
          <i className="pi pi-flag-fill" style={{ fontSize: 10 }} /> forgot checkout (hours counted till 7pm)
        </span>
      </div>

      <style>{`
        .dv2 { padding: 0.5rem 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; }
        .dv2-header { text-align: center; margin-bottom: 1rem; font-weight: 500; color: #333; font-size: 1.4rem; }

        .dv2-grid { border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; overflow: hidden; }
        .dv2-scale-row, .dv2-row { display: flex; border-bottom: 1px solid #e2e8f0; }
        .dv2-row:last-child { border-bottom: none; }

        .dv2-left { width: 210px; min-width: 210px; padding: 8px 12px; border-right: 1px solid #e2e8f0; background: #f8fafc; }
        .dv2-name { font-weight: 600; font-size: 0.9rem; color: #1e293b; }
        .dv2-att { font-size: 0.75rem; color: #475569; margin-top: 3px; }
        .dv2-absent { color: #94a3b8; font-style: italic; }
        .dv2-hours { font-size: 0.75rem; color: #64748b; margin-top: 3px; }
        .dv2-worked { color: #059669; font-weight: 600; }
        .dv2-logbtn { background: none; border: none; padding: 0; margin-top: 5px; font-size: 0.72rem; color: #2563eb; cursor: pointer; }

        .dv2-track { flex: 1; position: relative; background: #fff; overflow: visible; min-width: 0; }
        .dv2-canvas { position: relative; }
        .dv2-scale { height: 28px; }
        .dv2-tick { position: absolute; top: 0; bottom: 0; }
        .dv2-tick-label { position: absolute; top: 6px; left: 0; transform: translateX(-50%); font-size: 0.72rem; color: #64748b; white-space: nowrap; }
        .dv2-grid-line { position: absolute; top: 0; bottom: 0; width: 0; border-left: 1px dashed #eef2f7; z-index: 1; }

        .dv2-office { position: absolute; top: 0; bottom: 0; background: #ecfdf5; border-left: 1px solid #a7f3d0; border-right: 1px solid #a7f3d0; z-index: 0; }
        .dv2-office-forgot { border-right: 2px dashed #dc2626; }

        .dv2-marker { position: absolute; top: 0; bottom: 0; width: 0; z-index: 3; }
        .dv2-in { border-left: 2px solid #059669; }
        .dv2-out { border-left: 2px solid #475569; }
        .dv2-out-open { border-left: 2px dashed #f59e0b; }
        .dv2-out-forgot { border-left: 2px dashed #dc2626; }

        .dv2-flag { font-size: 0.72rem; color: #dc2626; font-weight: 600; margin-top: 3px; display: flex; align-items: center; }
        .dv2-flag-soft { color: #d97706; }
        .dv2-marker-label { position: absolute; top: 3px; left: 3px; font-size: 0.68rem; font-weight: 600; white-space: nowrap; }

        .dv2-gap-idle { position: absolute; background: #e2e8f0; opacity: 0.55; border-radius: 3px; z-index: 2; display: flex; align-items: center; justify-content: center; }
        .dv2-gap-label { font-size: 0.68rem; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }

        /* the paused stretch INSIDE a task bar */
        .dv2-pausebar { position: absolute; border-radius: 4px; z-index: 3; display: flex; align-items: center; justify-content: center;
          background: repeating-linear-gradient(45deg, #fecaca, #fecaca 5px, #fca5a5 5px, #fca5a5 10px); border: 1px solid #ef4444; }
        .dv2-pausebar-label { font-size: 0.72rem; font-weight: 700; color: #7f1d1d; white-space: nowrap; overflow: hidden; }

        /* the ✓ / review badge where a task closed */
        .dv2-mark { position: absolute; width: 18px; height: 18px; border-radius: 50%; z-index: 6; color: #fff;
          display: flex; align-items: center; justify-content: center; font-size: 10px; border: 2px solid #fff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3); }
        .dv2-mark .pi { font-size: 9px; }

        .dv2-seg { position: absolute; border-radius: 5px; z-index: 4; cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.25);
          border: 1px solid rgba(255,255,255,0.9); }
        .dv2-seg-out { opacity: 0.35; background-image: repeating-linear-gradient(45deg, rgba(255,255,255,0.4), rgba(255,255,255,0.4) 4px, transparent 4px, transparent 8px); }
        .dv2-seg:hover { filter: brightness(1.1); }

        .dv2-cluster { position: absolute; border-radius: 5px; z-index: 4; cursor: default; background: #475569;
          color: #fff; padding: 5px 7px; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,0.25);
          border: 1px solid rgba(255,255,255,0.9);
          display: flex; flex-direction: column; justify-content: center; }

        .dv2-seg-label { position: absolute; z-index: 5; display: flex; flex-direction: column; justify-content: center; padding: 4px 8px;
          color: #fff; overflow: hidden; cursor: pointer; pointer-events: auto; }

        .dv2-ext { position: absolute; z-index: 5; display: flex; align-items: center; overflow: hidden; cursor: pointer; }
        .dv2-ext-dot { width: 7px; height: 7px; border-radius: 50%; margin-right: 4px; flex: none; }
        .dv2-ext-text { font-size: 0.7rem; color: #334155; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          text-shadow: 0 0 3px rgba(255,255,255,0.9); }
        .dv2-seg-line1 { font-size: 0.79rem; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1.3; display: flex; align-items: center; }
        .dv2-seg-line2 { font-size: 0.71rem; opacity: 0.93; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1.3; margin-top: 2px; }

        .dv2-now { position: absolute; top: 0; bottom: 0; width: 0; border-left: 2px solid #ef4444; z-index: 6; }

        .dv2-absent-note { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: #94a3b8; font-size: 0.8rem; font-style: italic; }

        .dv2-log { background: #f8fafc; border-top: 1px solid #e2e8f0; padding: 6px 12px; max-height: 220px; overflow-y: auto; }
        .dv2-log-item { font-size: 0.78rem; color: #334155; padding: 2px 0; display: flex; align-items: center; }
        .dv2-log-time { font-weight: 600; color: #0f172a; min-width: 58px; font-variant-numeric: tabular-nums; }

        .dv2-legend { display: flex; justify-content: center; flex-wrap: wrap; gap: 18px; margin-top: 12px; font-size: 0.78rem; color: #475569; }
        .dv2-leg { display: inline-flex; align-items: center; gap: 6px; }
        .dv2-leg-swatch { display: inline-block; width: 14px; height: 14px; border-radius: 3px; }
        .dv2-leg-paused { background: repeating-linear-gradient(45deg, #fecaca, #fecaca 5px, #fca5a5 5px, #fca5a5 10px); border: 1px solid #ef4444; }
        .dv2-leg-mark { display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px;
          border-radius: 50%; color: #fff; border: 2px solid #fff; box-shadow: 0 1px 2px rgba(0,0,0,0.3); }
        .dv2-leg-mark .pi { font-size: 8px; }
      `}</style>
    </div>
  );
};

export default DayView;
